/**
 * Когда открытой вкладке спрашивать сервер в следующий раз.
 *
 * ⚠️ **Живёт в `lib/`, а не в комнате, потому что читателей уже трое:** отметки
 * «не понимаю» (`features/room/room-signals.ts`), лента чата занятия и счётчик
 * его непрочитанного (`features/occurrence-chat/*`). Правила ниже одни и те же
 * для всех троих, а разъехавшись по фичам, они разъедутся и по существу: у
 * одного опроса отступ при обрыве останется, у другого нет, и заметит это
 * только лежащий API.
 *
 * Дальше идёт разбор первого читателя, отметок «не понимаю», и он оставлен как
 * есть: правила выведены из него.
 *
 * **Зачем отдельный опрос, если у переписки такой уже есть.** Тот живёт в
 * каркасе кабинета (`Shell.tsx`), а комната стоит ВНЕ каркаса намеренно:
 * маршрут объявлен выше `Shell` в `App.tsx`, чтобы полоса «вышла новая версия»
 * не появлялась посреди урока. То есть преподаватель, сидящий в комнате, того
 * опроса не имеет вовсе, а отметка задумана ровно для четвёртого уровня
 * лестницы деградации, где он смотрит на доску и больше никуда
 * (`docs/group-room-design.md`, раздел 8). Переиспользуются ПРАВИЛА, а не хук.
 *
 * ⚠️ **Спрашивает только преподаватель, и второй опрос заводить не надо.**
 * Ученику он не нужен вовсе: своё состояние он знает сам, потому что сам его и
 * ставит. Единственное, чего он про себя не знает, это отметка, поставленная до
 * перезагрузки страницы, и она читается одним запросом при входе.
 *
 * **Правила, ради которых расписание живёт чистой функцией.** Каждое закрывает
 * свою поломку, и ни одна из них не видна на экране:
 *
 * 1. **скрытая вкладка не спрашивает вовсе**, а не «спрашивает реже». У
 *    человека с десятком фоновых вкладок это десяток запросов ни для кого.
 *    Возвращает опрос `visibilitychange`, и первый запрос после возвращения
 *    уходит немедленно, если прошёл интервал (`askOnWake`). При пятисекундном
 *    интервале это «всегда немедленно», и три первых читателя зовут запрос
 *    при возвращении сразу, без этой функции; у счётчика карточек интервал
 *    десять минут, и вкладка, переключённая туда и обратно, иначе спрашивала
 *    бы сервер на каждое переключение;
 * 2. **отступ при обрыве**: 10, 20, 40, 80 секунд и дальше по две минуты, но
 *    НЕ КОРОЧЕ интервала. Сеть в наших условиях рвётся штатно (раздел 3
 *    CLAUDE.md), и вкладка, долбящая лежащий API, мешает ему встать. Без пола
 *    по интервалу обрыв УЧАСТИЛ бы десятиминутный опрос до двух минут;
 * 3. **отсчёт от ОТВЕТА, а не от начала запроса.** `setInterval` на медленном
 *    канале выстраивает очередь запросов, которая уже не рассосётся;
 * 4. **ни одного запроса внахлёст.** Это правило живёт в самом хуке (флаг
 *    вокруг похода), потому что оно про порядок вызовов, а не про сроки.
 */

/**
 * Интервал опроса. Параметр у `nextAskMs` и `askOnWake` с этим умолчанием, и у
 * трёх первых читателей (отметки «не понимаю», лента и счётчик чата занятия)
 * поведение прежнее: они интервал не передают. Свой интервал у счётчика
 * карточек ученика, десять минут (10.2 `docs/flashcards-design.md`).
 *
 * Пять секунд между запросами, как у счётчика переписки.
 *
 * Отметка это просьба о помощи от ребёнка, который уже отстал: минута ожидания
 * тут дороже, чем несколько лишних запросов за занятие. Запрос при этом идёт по
 * первичному ключу и возвращает от нуля до двенадцати идентификаторов.
 */
export const POLL_MS = 5000;
const BACKOFF_START_MS = 10_000;
const BACKOFF_MAX_MS = 120_000;

/**
 * Через сколько миллисекунд спрашивать снова. `null` означает «не спрашивать и
 * таймер не заводить».
 *
 * ⚠️ `null` это не «подождать подольше». Планировать заход «на всякий случай»
 * значило бы держать таймер в каждой фоновой вкладке до конца сессии, а будит
 * вкладку событие, а не срок.
 */
export function nextAskMs(state: {
  /** Вкладка на экране. */
  visible: boolean;
  /** Сколько ответов подряд не пришло. Ноль означает «последний пришёл». */
  fails: number;
  /** Интервал опроса; без него `POLL_MS`. */
  intervalMs?: number;
}): number | null {
  if (!state.visible) return null;
  const interval = state.intervalMs ?? POLL_MS;
  if (state.fails <= 0) return interval;
  return Math.max(interval, Math.min(BACKOFF_MAX_MS, BACKOFF_START_MS * 2 ** (state.fails - 1)));
}

/**
 * Через сколько миллисекунд спрашивать, когда вкладка вернулась на экран
 * (правило 1). Ноль значит «немедленно».
 *
 * `sinceAnswerMs` это сколько прошло от последнего ПРИШЕДШЕГО ответа, а не от
 * последнего запроса (правило 3). Ответа ещё не было вовсе, значит `Infinity`,
 * и спрашиваем сразу: вернувшемуся человеку показать нечего.
 */
export function askOnWake(state: { sinceAnswerMs: number; intervalMs?: number }): number {
  const interval = state.intervalMs ?? POLL_MS;
  if (!(state.sinceAnswerMs < interval)) return 0;
  return Math.max(0, interval - state.sinceAnswerMs);
}
